import type { ImageSegmenter } from '@mediapipe/tasks-vision';

/**
 * Camera blur (#592): blurs everything behind the person in a camera track, in this browser, before the video is sent.
 * MediaPipe's selfie segmenter finds the person each frame; the rest is drawn blurred. The library and its WebAssembly
 * load only the first time someone turns blur on. A frame is never sent unblurred while blur is on: if a frame can't
 * be processed (e.g. the tab is in the background), the last blurred frame stays.
 */

const FPS = 15;
let segmenter: Promise<ImageSegmenter> | null = null;

function loadSegmenter(): Promise<ImageSegmenter> {
  segmenter ??= (async () => {
    const { FilesetResolver, ImageSegmenter } = await import('@mediapipe/tasks-vision');
    const files = await FilesetResolver.forVisionTasks('/mediapipe');
    const options = (delegate: 'GPU' | 'CPU') => ({
      baseOptions: { modelAssetPath: '/models/selfie_segmenter.tflite', delegate },
      runningMode: 'VIDEO' as const,
      outputCategoryMask: false,
      outputConfidenceMasks: true,
    });
    // The GPU is much faster where WebGL works; otherwise the CPU still keeps up at 15 frames a second.
    return ImageSegmenter.createFromOptions(files, options('GPU')).catch(() => ImageSegmenter.createFromOptions(files, options('CPU')));
  })();
  segmenter.catch(() => (segmenter = null)); // try again next time
  return segmenter;
}

export interface BlurredTrack {
  /** The blurred video, to send and to show as the preview. */
  track: MediaStreamTrack;
  /** Stops processing and the blurred track (not the camera track it came from). */
  stop: () => void;
}

/** A blurred copy of a camera track. Throws if blur can't start (old browser, no WebAssembly). */
export async function blurTrack(source: MediaStreamTrack, amount = 12): Promise<BlurredTrack> {
  const seg = await loadSegmenter();
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([source]);
  await video.play();
  const w = video.videoWidth || 640;
  const h = video.videoHeight || 480;

  const out = document.createElement('canvas');
  out.width = w;
  out.height = h;
  const ctx = out.getContext('2d')!;
  // The person, cut out with the mask.
  const person = document.createElement('canvas');
  person.width = w;
  person.height = h;
  const pctx = person.getContext('2d')!;
  // The mask at the model's size, as alpha.
  const mask = document.createElement('canvas');
  const mctx = mask.getContext('2d')!;
  let maskData: ImageData | null = null;

  const track = out.captureStream(FPS).getVideoTracks()[0]!;
  let stopped = false;
  let last = -1;

  const frame = () => {
    if (stopped) return;
    const now = performance.now();
    if (video.readyState >= 2 && video.currentTime !== last) {
      last = video.currentTime;
      try {
        seg.segmentForVideo(video, now, (result) => {
          const m = result.confidenceMasks?.[0];
          if (!m) return;
          if (!maskData || maskData.width !== m.width || maskData.height !== m.height) {
            mask.width = m.width;
            mask.height = m.height;
            maskData = mctx.createImageData(m.width, m.height);
          }
          const conf = m.getAsFloat32Array();
          const px = maskData.data;
          for (let i = 0; i < conf.length; i++) px[i * 4 + 3] = conf[i]! * 255;
          mctx.putImageData(maskData, 0, 0);
          m.close();
        });
        // Background: the whole frame, blurred.
        ctx.filter = `blur(${amount}px)`;
        ctx.drawImage(video, 0, 0, w, h);
        ctx.filter = 'none';
        // The person on top, sharp: the frame kept only where the (softened) mask says.
        pctx.globalCompositeOperation = 'copy';
        pctx.drawImage(video, 0, 0, w, h);
        pctx.globalCompositeOperation = 'destination-in';
        pctx.filter = 'blur(2px)';
        pctx.drawImage(mask, 0, 0, w, h);
        pctx.filter = 'none';
        ctx.drawImage(person, 0, 0);
      } catch {
        // Keep the last blurred frame rather than send an unblurred one.
      }
    }
    setTimeout(frame, 1000 / FPS);
  };
  frame();

  const stop = () => {
    stopped = true;
    track.stop();
    video.srcObject = null;
  };
  source.addEventListener('ended', stop);
  return { track, stop };
}

/** Whether this browser can blur at all (WebAssembly and canvas capture). */
export function canBlur(): boolean {
  return typeof WebAssembly === 'object' && typeof HTMLCanvasElement !== 'undefined' && 'captureStream' in HTMLCanvasElement.prototype;
}

/** The person's own choice, remembered in this browser; unset follows the server's default. */
export function blurChoice(key: string): boolean | null {
  try {
    const v = localStorage.getItem(key);
    return v === null ? null : v === 'on';
  } catch {
    return null;
  }
}
export function saveBlurChoice(key: string, on: boolean) {
  try {
    localStorage.setItem(key, on ? 'on' : 'off');
  } catch {
    /* private mode: just this visit */
  }
}
